import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  CircularProgress,
  Fab,
  Paper,
  Chip,
  IconButton,
  ButtonGroup,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  LocalShipping as ContainerIcon,
  Schedule as TimeIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api, API_URL } from '../config/api';
import axios from 'axios';
import DailyContainerReport from './DailyContainerReport';
import ContainerNav from './ContainerNav';

interface Container {
  id: number;
  container_no: string;
  seal_no: string;
  delivery_day: string;
  delivery_time: string;
  dock: string;
  location: string;
  status: string;
  line_count: number;

  // Multi-PO fields (derived from container_lines)
  is_shared: boolean;
  po_numbers: string[];
  primary_po_number: string | null;
  po_summary: Array<{
    po_number: string;
    line_count: number;
    unique_stocks: number;
  }>;
  supplier_code?: string;
  chain_of_custody?: string;
  gr_complete?: boolean;
  // Completion fields from container_completions JOIN
  completed_at?: string | null;
  arrival_time?: string | null;
  seal_verified?: boolean | null;
}

interface ContainerLine {
  id: number;
  container_id: number;
  stock_code: string;
  rolls_per_pallet: number;
  sheets: number;
  metres: number;
  net_weight_tonnes: number;
  certification: string;
  prepopulated_qty: number;
}

interface LineLocation {
  stockCode: string;
  location: string;
  quantity: number;
}

interface PreplanRecommendation {
  stock_code: string;
  location_name: string;
  planned_quantity: number;
  consumed_quantity: number;
  remaining_quantity: number;
  recommended_quantity: number;
  is_flexible?: boolean;  // True for flexible pre-plans (quantity=0, operator decides)
  estimated_measurement_value: number;
  estimated_measurement_unit: string;
  notes: string;
  created_by_name: string;
}

interface GroupedLine {
  stockCode: string;
  totalPallets: number;
  totalSheets: number;
  totalMetres: number;
  totalWeightKg: number;
  isReel: boolean;
}

interface ContainerCompletion {
  id: number;
  container_id: number;
  seal_verified: boolean;
  condition_ok: boolean;
  water_free: boolean;
  pallets_received: number | null;
  arrival_time: string;
  departure_time: string;
  signed_by_staff_no: string;
  signed_by_name?: string;
  signature_url: string;
  completed_at: string;
}

interface SignaturePadProps {
  onSave: (signature: string) => void;
  onClear: () => void;
}

// Helper to check if container is received (handles old, new, and archived status values)
const isContainerReceived = (status: string | undefined): boolean => {
  return status === 'received' || status === 'Completed' || status === 'Finalised' || status === 'archived';
};

// Helper to check if container inspection is in progress (draft completion exists but not finalised)
const isContainerInProgress = (container: Container): boolean => {
  if (isContainerReceived(container.status)) return false;
  // A draft completion record exists if any completion field is populated but completed_at is null
  return !container.completed_at && (container.arrival_time != null || container.seal_verified != null);
};

const SignaturePad: React.FC<SignaturePadProps> = ({ onSave, onClear }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.strokeStyle = '#000000';
    context.lineWidth = 2;
    context.lineCap = 'round';
  }, []);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.beginPath();
    context.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.lineTo(x, y);
    context.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataURL = canvas.toDataURL('image/png');
    onSave(dataURL);
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.clearRect(0, 0, canvas.width, canvas.height);
    onClear();
  };

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 1.5 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Driver Signature</Typography>
        <canvas
          ref={canvasRef}
          width={400}
          height={150}
          style={{ border: '1px solid #ccc', width: '100%', height: '150px', touchAction: 'none' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <Box mt={1} display="flex" gap={2}>
          <Button variant="outlined" onClick={handleClear} size="small">Clear</Button>
          <Button variant="contained" onClick={handleSave} size="small">Save Signature</Button>
        </Box>
      </Paper>
    </Box>
  );
};

type DateRange = 'today' | '3days' | '7days';

// Helper function to get current datetime in local timezone for datetime-local input
const getLocalDateTimeString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

// Helper function to convert UTC/server datetime to local datetime-local format
const formatDateTimeForInput = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const ForkliftContainerApp: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [containers, setContainers] = useState<Container[]>([]);
  const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);
  const [containerLines, setContainerLines] = useState<ContainerLine[]>([]);
  const [existingCompletion, setExistingCompletion] = useState<ContainerCompletion | null>(null);
  const [savedLineLocations, setSavedLineLocations] = useState<any[]>([]);
  const [savedPhotos, setSavedPhotos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completionDialog, setCompletionDialog] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>('today');
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, -1 = previous week, 1 = next week

  // Completion form state
  const [completionData, setCompletionData] = useState<{
    seal_verified: boolean | null;
    condition_ok: boolean | null;
    water_free: boolean | null;
    pallets_received: string;
    arrival_time: string;
    departure_time: string;
    signature_url: string;
  }>({
    seal_verified: null,
    condition_ok: null,
    water_free: null,
    pallets_received: '',
    arrival_time: getLocalDateTimeString(),
    departure_time: '',
    signature_url: ''
  });

  const [sealPhoto, setSealPhoto] = useState<File | null>(null);
  const [sealPhotoPreview, setSealPhotoPreview] = useState<string>('');
  const [concernPhotos, setConcernPhotos] = useState<File[]>([]);
  const [concernPhotosPreviews, setConcernPhotosPreviews] = useState<string[]>([]);
  const [lineLocations, setLineLocations] = useState<LineLocation[]>([]);
  const [preplanRecommendations, setPreplanRecommendations] = useState<PreplanRecommendation[]>([]);

  // Daily report state
  const [dailyReportOpen, setDailyReportOpen] = useState(false);
  const [dailyReportDate, setDailyReportDate] = useState<string>('');
  const [dailyReportData, setDailyReportData] = useState<any[]>([]);
  const [loadingReport, setLoadingReport] = useState(false);

  // Email schedule state
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const sealFileInputRef = useRef<HTMLInputElement>(null);
  const concernFileInputRef = useRef<HTMLInputElement>(null);

  // Use API_URL which includes /api path

  // Dock list (5 docks)
  const docks = ['Dock 1', 'Dock 2', 'Dock 3', 'Dock 4', 'Dock 5'];

  const dockDisplayMap: { [key: string]: string } = {
    'Dock 1': 'Dock 1 (Building 3)',
    'Dock 2': 'Dock 2 (Building 4)',
    'Dock 3': 'Dock 3 (Building 2)',
    'Dock 4': 'Dock 4 (Building 1)',
    'Dock 5': 'Dock 5 (Building 5)'
  };

  const getDockDisplayName = (dockValue: string) => {
    return dockDisplayMap[dockValue] || dockValue;
  };

  useEffect(() => {
    loadContainers(dateRange);
  }, [dateRange, weekOffset]);

  const loadContainers = async (range: DateRange) => {
    try {
      setLoading(true);

      // Calculate start date based on weekOffset (7 days per offset)
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() + (weekOffset * 7));
      const startDateStr = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;

      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6); // Always show 7 days
      const endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

      // Ask for just this week. This used to fetch all 915 containers (780 KB)
      // to display the nine in the current week, on every arrow click.
      const response = await api.get(
        `/containers?date_from=${startDateStr}&date_to=${endDateStr}`);
      if (response.data.success) {
        const filtered = response.data.containers.filter((c: Container) => {
          if (!c.delivery_day) return false;
          // Compare date strings directly (YYYY-MM-DD format)
          return c.delivery_day >= startDateStr && c.delivery_day <= endDateStr;
        });
        setContainers(filtered);
      } else {
        setError('Failed to load containers');
      }
    } catch (err) {
      setError('Error loading containers');
      console.error('Error loading containers:', err);
    } finally {
      setLoading(false);
    }
  };

  const startCompletion = async (container: Container) => {
    setSelectedContainer(container);

    // Reset ALL state immediately to prevent cross-contamination between containers
    if (sealPhotoPreview) URL.revokeObjectURL(sealPhotoPreview);
    concernPhotosPreviews.forEach(url => URL.revokeObjectURL(url));
    setSealPhoto(null);
    setSealPhotoPreview('');
    setConcernPhotos([]);
    setConcernPhotosPreviews([]);
    setSavedPhotos([]);  // Reset saved photos from previous container
    setSavedLineLocations([]);  // Reset saved line locations from previous container
    setLineLocations([]);  // Reset editable line locations
    setExistingCompletion(null);  // Reset completion state
    setPreplanRecommendations([]);  // Reset preplan recommendations

    // Fetch container details including lines
    try {
      const response = await api.get(`/containers/${container.id}`);
      if (response.data.success) {
        const lines = response.data.lines || [];
        setContainerLines(lines);
        // Initialize empty location entries
        setLineLocations([]);
      }
    } catch (err) {
      console.error('Error loading container details:', err);
      setError('Error loading container details');
    }

    // Fetch preplan recommendations for this container
    try {
      const preplanResponse = await api.get(`/containers/${container.id}/preplan-recommendations`);
      if (preplanResponse.data.success) {
        setPreplanRecommendations(preplanResponse.data.recommendations || []);
      } else {
        setPreplanRecommendations([]);
      }
    } catch (err) {
      console.error('Error loading preplan recommendations:', err);
      setPreplanRecommendations([]);
    }

    // Try to fetch completion data (draft or final) for any container
    try {
      const completionResponse = await api.get(`/containers/${container.id}/completion`);

      if (completionResponse.data.success) {
        const completionData = completionResponse.data.completion;

        // If container is completed, set as read-only
        if (isContainerReceived(container.status)) {
          setExistingCompletion(completionData);
        } else {
          // Container not completed yet, but has draft data - load it into the form
          setExistingCompletion(null); // Keep form editable
          setCompletionData({
            seal_verified: completionData.seal_verified !== undefined ? completionData.seal_verified : null,
            condition_ok: completionData.condition_ok !== undefined ? completionData.condition_ok : null,
            water_free: completionData.water_free !== undefined ? completionData.water_free : null,
            pallets_received: completionData.pallets_received || '',
            arrival_time: formatDateTimeForInput(completionData.arrival_time) || getLocalDateTimeString(),
            departure_time: formatDateTimeForInput(completionData.departure_time),
            signature_url: completionData.signature_url || ''
          });
        }
      }
    } catch (err) {
      // No draft data found - initialize blank form
      console.log('No completion data found, starting fresh');
      setCompletionData({
        seal_verified: null,
        condition_ok: null,
        water_free: null,
        pallets_received: '',
        arrival_time: getLocalDateTimeString(),
        departure_time: '',
        signature_url: ''
      });
    }

    // Always fetch line locations for this container (separate from completion data)
    try {
      const locationsResponse = await api.get(`/containers/${container.id}/line-locations`);
      if (locationsResponse.data.success) {
        const savedLocations = locationsResponse.data.line_locations;
        setSavedLineLocations(savedLocations);

        // If container is NOT completed, populate the editable form with saved line locations
        if (container.status !== 'received' && savedLocations.length > 0) {
          const editableLocations = savedLocations.map((loc: any) => ({
            stockCode: loc.stock_code,
            location: loc.location_name,
            quantity: loc.quantity
          }));
          setLineLocations(editableLocations);
        }
      }
    } catch (err) {
      console.log('No line locations found');
    }

    // Always fetch photos for this container (separate from completion data)
    try {
      const photosResponse = await api.get(`/containers/${container.id}/photos`);
      if (photosResponse.data.success) {
        setSavedPhotos(photosResponse.data.photos || []);
      }
    } catch (err) {
      console.log('No photos found');
    }

    setCompletionDialog(true);
  };

  const uploadPhotoToBlob = async (file: File): Promise<string> => {
    try {
      // Request SAS URL from backend
      const response = await api.post(`/containers/generate-upload-url`, {
        filename: file.name,
        content_type: file.type
      });

      const { upload_url, blob_url } = response.data;

      // Upload file to blob storage using SAS URL
      await axios.put(upload_url, file, {
        headers: {
          'Content-Type': file.type,
          'x-ms-blob-type': 'BlockBlob'
        }
      });

      return blob_url;
    } catch (error) {
      console.error('Error uploading photo to blob storage:', error);
      throw error;
    }
  };

  const handleSealPhotoCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedContainer) return;

    try {
      // Show preview immediately for feedback
      if (sealPhotoPreview) {
        URL.revokeObjectURL(sealPhotoPreview);
      }
      setSealPhoto(file);
      setSealPhotoPreview(URL.createObjectURL(file));

      // Delete any existing seal photo first (UPSERT behavior)
      const existingSealPhoto = savedPhotos.find(p => p.photo_type === 'seal');
      if (existingSealPhoto) {
        await api.delete(`/containers/${selectedContainer.id}/photos`, {
          data: { photo_id: existingSealPhoto.id }
        });
      }

      // Upload to blob storage
      const blobUrl = await uploadPhotoToBlob(file);

      // Save to database immediately
      const response = await api.post(`/containers/${selectedContainer.id}/photos`, {
        photo_url: blobUrl,
        photo_type: 'seal',
        uploaded_by_staff_no: user?.staff_no
      });

      // Update savedPhotos state with the new photo
      const newPhoto = {
        id: response.data.photo_id,
        photo_url: blobUrl,
        photo_type: 'seal',
        uploaded_at: response.data.uploaded_at
      };
      setSavedPhotos(prev => [
        ...prev.filter(p => p.photo_type !== 'seal'),
        newPhoto
      ]);

      // Clear the local file state since it's now saved
      setSealPhoto(null);
      if (sealPhotoPreview) {
        URL.revokeObjectURL(sealPhotoPreview);
      }
      setSealPhotoPreview('');

    } catch (err) {
      console.error('Error uploading seal photo:', err);
      setError('Failed to upload seal photo. Please try again.');
      // Clear the preview on error
      setSealPhoto(null);
      if (sealPhotoPreview) {
        URL.revokeObjectURL(sealPhotoPreview);
      }
      setSealPhotoPreview('');
    }
  };

  const handleConcernPhotosCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const filesArray = Array.from(files);
      // Create previews for new files
      const newPreviews = filesArray.map(file => URL.createObjectURL(file));
      setConcernPhotos(prev => [...prev, ...filesArray]);
      setConcernPhotosPreviews(prev => [...prev, ...newPreviews]);
    }
  };

  // Group container lines by stock code
  const groupContainerLines = (): GroupedLine[] => {
    const grouped = containerLines.reduce((acc: Record<string, GroupedLine>, line) => {
      if (!acc[line.stock_code]) {
        // Determine if this is a reel (no 'X' in code)
        const isReel = !line.stock_code.includes('X');
        acc[line.stock_code] = {
          stockCode: line.stock_code,
          totalPallets: 0,
          totalSheets: 0,
          totalMetres: 0,
          totalWeightKg: 0,
          isReel
        };
      }
      acc[line.stock_code].totalPallets += 1; // Each line represents one pallet or reel
      acc[line.stock_code].totalSheets += line.sheets || 0;
      acc[line.stock_code].totalMetres += line.metres || 0;
      acc[line.stock_code].totalWeightKg += (line.net_weight_tonnes || 0) * 1000;
      return acc;
    }, {});
    return Object.values(grouped);
  };

  const addLocationEntry = (stockCode: string) => {
    setLineLocations(prev => [...prev, { stockCode, location: '', quantity: 0 }]);
    // Note: Don't auto-save yet - entry is empty until user fills it in
  };

  const updateLocationEntry = (index: number, field: 'location' | 'quantity', value: string | number) => {
    setLineLocations(prev => prev.map((entry, i) =>
      i === index ? { ...entry, [field]: value } : entry
    ));
    // Note: Auto-save now happens onBlur, not onChange
  };

  const removeLocationEntry = (index: number) => {
    setLineLocations(prev => prev.filter((_, i) => i !== index));
    // Auto-save immediately after removing (single action, not typing)
    setTimeout(() => saveLineLocationsDraft(), 100);
  };

  const applyRecommendation = (recommendation: PreplanRecommendation) => {
    // Add the recommended location to lineLocations
    // For flexible pre-plans (is_flexible=true), set quantity to 0 so operator can fill it in
    setLineLocations(prev => [
      ...prev,
      {
        stockCode: recommendation.stock_code,
        location: recommendation.location_name,
        quantity: recommendation.is_flexible ? 0 : recommendation.recommended_quantity
      }
    ]);
    // Auto-save immediately after applying (single action, not typing)
    setTimeout(() => saveLineLocationsDraft(), 100);
  };

  const handleSignatureSave = (signature: string) => {
    setCompletionData(prev => ({ ...prev, signature_url: signature }));
  };

  const handleGenerateDailyReport = async (reportDate: string) => {
    try {
      setLoadingReport(true);
      setDailyReportDate(reportDate);

      const response = await api.get(`/containers/daily-report/${reportDate}`);

      if (response.data.success) {
        setDailyReportData(response.data.containers);
        setDailyReportOpen(true);
      } else {
        setError('Failed to generate report');
      }
    } catch (err) {
      console.error('Error generating daily report:', err);
      setError('Error generating daily report');
    } finally {
      setLoadingReport(false);
    }
  };

  // Auto-save draft data when fields change
  const saveDraft = async (partialData?: any) => {
    if (!selectedContainer) return;

    try {
      const draftPayload = {
        ...completionData,
        ...partialData,
        draft: true  // Mark as draft save
      };

      await api.put(`/containers/${selectedContainer.id}/complete`, draftPayload);
      console.log('Draft saved successfully');
    } catch (err) {
      console.error('Error saving draft:', err);
      // Fail silently - don't disrupt user experience
    }
  };

  // Auto-save line locations as draft
  const saveLineLocationsDraft = async () => {
    if (!selectedContainer) return;

    try {
      const groupedLines = groupContainerLines();

      // Only save valid locations (have both location and quantity > 0)
      const locationsWithMeasurements = lineLocations
        .filter(loc => loc.location && loc.quantity > 0)
        .map(loc => {
          const groupedLine = groupedLines.find(g => g.stockCode === loc.stockCode);
          if (!groupedLine) {
            return null;
          }

          // Calculate proportional measurement
          const proportion = loc.quantity / groupedLine.totalPallets;
          let measurement_value = null;
          let measurement_unit = null;

          if (groupedLine.isReel) {
            // For reels, use kg
            measurement_value = Math.round(groupedLine.totalWeightKg * proportion);
            measurement_unit = 'kg';
          } else {
            // For sheets, use sheets
            measurement_value = Math.round(groupedLine.totalSheets * proportion);
            measurement_unit = 'sheets';
          }

          return {
            stock_code: loc.stockCode,
            location: loc.location,
            quantity: loc.quantity,
            measurement_value,
            measurement_unit
          };
        })
        .filter(loc => loc !== null);

      if (locationsWithMeasurements.length > 0) {
        await api.post(`/containers/${selectedContainer.id}/line-locations`, {
          line_locations: locationsWithMeasurements
        });
      }
    } catch (err) {
      console.error('Error saving line locations draft:', err);
      // Fail silently - don't disrupt user experience
    }
  };

  const submitCompletion = async () => {
    if (!selectedContainer || !user?.staff_no) {
      setError('Missing required information');
      return;
    }

    if (!completionData.signature_url) {
      setError('Driver signature is required');
      return;
    }

    // Check for saved seal photo (photos are now uploaded immediately when captured)
    const hasSealPhoto = savedPhotos.some(p => p.photo_type === 'seal');
    if (!hasSealPhoto) {
      setError('Seal photo is required');
      return;
    }

    // Save any unsaved line locations BEFORE validation
    // This ensures that locations entered just before clicking Complete are saved
    try {
      await saveLineLocationsDraft();
    } catch (err) {
      console.error('Error saving line locations before completion:', err);
    }

    // Validate all stock has been put away
    const groupedLines = groupContainerLines();
    const incompletePutAways: string[] = [];
    const overCountErrors: string[] = [];

    for (const groupedLine of groupedLines) {
      const stockLocations = lineLocations.filter(l => l.stockCode === groupedLine.stockCode);
      const totalRecorded = stockLocations.reduce((sum, loc) => sum + (loc.quantity || 0), 0);

      if (totalRecorded < groupedLine.totalPallets) {
        const remaining = groupedLine.totalPallets - totalRecorded;
        incompletePutAways.push(
          `${groupedLine.stockCode}: ${remaining} of ${groupedLine.totalPallets} ${groupedLine.isReel ? 'reels' : 'pallets'} not put away`
        );
      } else if (totalRecorded > groupedLine.totalPallets) {
        const over = totalRecorded - groupedLine.totalPallets;
        overCountErrors.push(
          `${groupedLine.stockCode}: ${over} extra ${groupedLine.isReel ? 'reels' : 'pallets'} recorded (expected ${groupedLine.totalPallets}, recorded ${totalRecorded})`
        );
      }
    }

    if (incompletePutAways.length > 0) {
      setError(
        'Cannot complete inspection - not all stock has been put away:\n\n' +
        incompletePutAways.join('\n')
      );
      return;
    }

    if (overCountErrors.length > 0) {
      setError(
        'Cannot complete inspection - recorded quantities exceed expected:\n\n' +
        overCountErrors.join('\n')
      );
      return;
    }

    // Validate all inspection questions are answered
    const unansweredQuestions: string[] = [];
    if (completionData.seal_verified === null) {
      unansweredQuestions.push('Seal Verified and Intact');
    }
    if (completionData.water_free === null) {
      unansweredQuestions.push('Container Free of Water');
    }
    if (completionData.condition_ok === null) {
      unansweredQuestions.push('Pallets in Good Condition');
    }

    if (unansweredQuestions.length > 0) {
      setError(
        'Please answer all inspection questions before completing:\n\n' +
        unansweredQuestions.map(q => `• ${q}`).join('\n')
      );
      return;
    }

    try {
      // Use existing departure time if set, otherwise use current time
      const finalDepartureTime = completionData.departure_time || getLocalDateTimeString();

      const payload = {
        ...completionData,
        pallets_received: completionData.pallets_received ? parseInt(completionData.pallets_received) : null,
        signed_by_staff_no: user.staff_no,
        departure_time: finalDepartureTime
      };

      setLoading(true);

      // Upload concern photos to blob storage and save to DB BEFORE completion
      // (so the completion email can detect concern photos and adjust subject/importance)
      for (const photo of concernPhotos) {
        const url = await uploadPhotoToBlob(photo);
        await api.post(`/containers/${selectedContainer.id}/photos`, {
          photo_url: url,
          photo_type: 'damage',
          uploaded_by_staff_no: user.staff_no
        });
      }

      // Complete the container inspection. The materials-team email is queued
      // here and sent by a backend timer within a minute or so (migration 139),
      // so this returns in well under a second instead of the old 15-50 s.
      await api.put(`/containers/${selectedContainer.id}/complete`, payload);

      // Save line locations with proportional measurements
      if (lineLocations.length > 0) {
        try {
          const groupedLines = groupContainerLines();

          const locationsWithMeasurements = lineLocations
            .filter(loc => loc.location && loc.quantity > 0)
            .map(loc => {
              const groupedLine = groupedLines.find(g => g.stockCode === loc.stockCode);
              if (!groupedLine) {
                console.warn(`No grouped line found for stock code: ${loc.stockCode}`);
                return null;
              }

              // Calculate proportional measurement
              const proportion = loc.quantity / groupedLine.totalPallets;
              let measurement_value = null;
              let measurement_unit = null;

              if (groupedLine.isReel) {
                // For reels, use kg
                measurement_value = Math.round(groupedLine.totalWeightKg * proportion);
                measurement_unit = 'kg';
              } else {
                // For sheets, use sheets
                measurement_value = Math.round(groupedLine.totalSheets * proportion);
                measurement_unit = 'sheets';
              }

              return {
                stock_code: loc.stockCode,
                location: loc.location,
                quantity: loc.quantity,
                measurement_value,
                measurement_unit
              };
            })
            .filter(loc => loc !== null);

          if (locationsWithMeasurements.length > 0) {
            console.log('Saving line locations:', locationsWithMeasurements);
            await api.post(`/containers/${selectedContainer.id}/line-locations`, {
              line_locations: locationsWithMeasurements
            });
          }
        } catch (locErr) {
          console.error('Error saving line locations:', locErr);
          // Don't throw - continue with rest of completion
        }
      }

      setCompletionDialog(false);
      setSelectedContainer(null);
      loadContainers(dateRange);

      // Show success message
      setError(null);

      // Clean up preview URLs
      if (sealPhotoPreview) URL.revokeObjectURL(sealPhotoPreview);
      concernPhotosPreviews.forEach(url => URL.revokeObjectURL(url));

    } catch (err) {
      setError('Error completing container inspection');
      console.error('Error completing container:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Generate 7 days array starting from today + weekOffset
  const generateWeekDays = () => {
    const days = [];
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() + (weekOffset * 7));

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      // Check if this date is today
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const isToday = dateStr === todayStr;

      days.push({
        date: dateStr,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: date.getDate(),
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        isToday
      });
    }
    return days;
  };

  const weekDays = generateWeekDays();

  // Navigation handlers
  const handlePreviousWeek = () => setWeekOffset(prev => prev - 1);
  const handleNextWeek = () => setWeekOffset(prev => prev + 1);
  const handleGoToToday = () => setWeekOffset(0);

  // Send daily schedule email
  const handleSendScheduleEmail = async () => {
    setSendingEmail(true);
    setEmailSuccess(null);
    setEmailError(null);

    try {
      const response = await api.post('/email/daily-schedule', {});

      if (response.data.success) {
        setEmailSuccess(`Schedule email sent to ${response.data.recipients?.join(', ') || 'recipients'}`);
        // Clear success message after 5 seconds
        setTimeout(() => setEmailSuccess(null), 5000);
      } else {
        setEmailError(response.data.error || 'Failed to send email');
      }
    } catch (err: any) {
      console.error('Error sending schedule email:', err);
      setEmailError(err.response?.data?.error || err.message || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Helper to get containers for specific dock and date
  const getContainersForCell = (dock: string, dateStr: string) => {
    return containers.filter(c => c.dock === dock && c.delivery_day === dateStr);
  };

  return (
    <Box p={2}>
      {/* Header */}
      <ContainerNav
        rightContent={
          <Tooltip title="Send 7-day schedule email with F-136 PDF" arrow>
            <span>
              <Button
                variant="outlined"
                color="primary"
                startIcon={sendingEmail ? <CircularProgress size={16} /> : <EmailIcon />}
                onClick={handleSendScheduleEmail}
                disabled={sendingEmail}
                size="small"
              >
                {sendingEmail ? 'Sending...' : 'Email Schedule'}
              </Button>
            </span>
          </Tooltip>
        }
      />

      {/* Email Status Messages */}
      {emailSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEmailSuccess(null)}>
          {emailSuccess}
        </Alert>
      )}
      {emailError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEmailError(null)}>
          {emailError}
        </Alert>
      )}

      {/* Calendar Navigation */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2, mt: 1 }}>
        <IconButton onClick={handlePreviousWeek} color="primary" size="large">
          <ChevronLeftIcon fontSize="large" />
        </IconButton>

        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {weekDays[0]?.month} {weekDays[0]?.dayNum} - {weekDays[6]?.month} {weekDays[6]?.dayNum}
            {weekDays[0]?.date.substring(0, 4) !== weekDays[6]?.date.substring(0, 4) && (
              <span>, {weekDays[6]?.date.substring(0, 4)}</span>
            )}
          </Typography>
          {weekOffset !== 0 && (
            <Button
              variant="outlined"
              startIcon={<TodayIcon />}
              onClick={handleGoToToday}
              size="small"
            >
              Today
            </Button>
          )}
        </Box>

        <IconButton onClick={handleNextWeek} color="primary" size="large">
          <ChevronRightIcon fontSize="large" />
        </IconButton>
      </Box>

      {/* Calendar Grid */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 900, borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#CB6015', minWidth: 120, border: '1px solid #ddd' }}>
                {/* Empty top-left cell */}
              </TableCell>
              {weekDays.map((day) => (
                <TableCell
                  key={day.date}
                  align="center"
                  sx={{
                    backgroundColor: day.isToday ? '#A54E11' : '#CB6015',
                    color: 'white',
                    fontWeight: 'bold',
                    minWidth: 140,
                    border: day.isToday ? '3px solid #FFD700' : '1px solid #ddd',
                    boxShadow: day.isToday ? 'inset 0 0 10px rgba(255, 215, 0, 0.5)' : 'none'
                  }}
                >
                  <Box>
                    <Typography variant="caption" display="block">{day.dayName}</Typography>
                    <Typography variant="h6">{day.dayNum}</Typography>
                    <Typography variant="caption">{day.month}</Typography>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {docks.map((dock) => (
              <TableRow key={dock}>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#fff', border: '1px solid #ddd' }}>
                  {getDockDisplayName(dock)}
                </TableCell>
                {weekDays.map((day) => {
                  const cellContainers = getContainersForCell(dock, day.date);
                  return (
                    <TableCell
                      key={`${dock}-${day.date}`}
                      sx={{
                        p: 0.5,
                        verticalAlign: 'top',
                        minHeight: 80,
                        backgroundColor: '#fff',
                        border: '1px solid #ddd'
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        {cellContainers.map((container) => {
                          const inProgress = isContainerInProgress(container);
                          const received = isContainerReceived(container.status);
                          const borderColor = received ? '#4caf50' : inProgress ? '#ed6c02' : '#CB6015';
                          const bgColor = received ? '#f1f8f4' : inProgress ? '#fff8e1' : 'transparent';
                          const hoverBg = received ? '#e8f5e9' : inProgress ? '#fff3cd' : '#FFF8F0';
                          const hoverBorder = received ? '#4caf50' : inProgress ? '#e65100' : '#A54E11';
                          return (
                          <Button
                            key={container.id}
                            onClick={() => startCompletion(container)}
                            variant="outlined"
                            size="small"
                            sx={{
                              textAlign: 'left',
                              justifyContent: 'flex-start',
                              p: 0.5,
                              fontSize: '0.7rem',
                              lineHeight: 1.2,
                              textTransform: 'none',
                              borderColor,
                              backgroundColor: bgColor,
                              '&:hover': {
                                backgroundColor: hoverBg,
                                borderColor: hoverBorder
                              }
                            }}
                          >
                            <Box width="100%" display="flex" alignItems="flex-start" gap={0.5}>
                              <Box flex={1}>
                                <Typography variant="caption" display="block" fontWeight="bold">
                                  {container.container_no}
                                </Typography>
                                {container.seal_no && (
                                  <Typography variant="caption" display="block" sx={{ color: '#0288d1', fontWeight: 500 }}>
                                    Seal #: {container.seal_no}
                                  </Typography>
                                )}
                                <Typography variant="caption" display="block">
                                  {container.delivery_time || 'No time'}
                                </Typography>
                                {container.arrival_time && (
                                  <Typography variant="caption" display="block" sx={{
                                    color: '#2e7d32',
                                    fontWeight: 500,
                                    ...(!inProgress && { display: 'none', '.MuiButton-root:hover &': { display: 'block' } })
                                  }}>
                                    Arrived: {new Date(container.arrival_time).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true })}
                                  </Typography>
                                )}
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {container.supplier_code || 'No supplier'} •
                                  {container.is_shared ? (
                                    ` POs ${container.po_numbers.join(', ')}`
                                  ) : (
                                    ` PO ${container.primary_po_number || 'N/A'}`
                                  )}
                                </Typography>
                              </Box>
                              <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.25} flexShrink={0}>
                                {container.gr_complete && (
                                  <Tooltip title="Goods Receipted in Prism">
                                    <Chip label="GR" size="small" sx={{ fontSize: '0.6rem', fontWeight: 'bold', height: 16, backgroundColor: '#7b1fa2', color: '#fff' }} />
                                  </Tooltip>
                                )}
                                {inProgress && (
                                  <Tooltip title="Inspection in progress — not yet completed">
                                    <Chip label="In Progress" size="small" sx={{ fontSize: '0.55rem', fontWeight: 'bold', height: 16, backgroundColor: '#ed6c02', color: '#fff' }} />
                                  </Tooltip>
                                )}
                                {received && (
                                  <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50', flexShrink: 0 }} />
                                )}
                              </Box>
                            </Box>
                          </Button>
                          );
                        })}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}

            {/* Daily Report Row */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', border: '1px solid #ddd', textAlign: 'center' }}>
                Daily Report
              </TableCell>
              {weekDays.map((day) => {
                // Count completed containers for this date
                const completedCount = containers.filter(c =>
                  c.delivery_day?.startsWith(day.date) && isContainerReceived(c.status)
                ).length;

                return (
                  <TableCell
                    key={`report-${day.date}`}
                    sx={{
                      p: 0.5,
                      textAlign: 'center',
                      backgroundColor: '#f5f5f5',
                      border: '1px solid #ddd'
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      disabled={completedCount === 0 || loadingReport}
                      onClick={() => handleGenerateDailyReport(day.date)}
                      sx={{
                        fontSize: '0.7rem',
                        backgroundColor: '#CB6015',
                        '&:hover': {
                          backgroundColor: '#A54E11'
                        },
                        '&:disabled': {
                          backgroundColor: '#e0e0e0'
                        }
                      }}
                    >
                      {loadingReport && dailyReportDate === day.date ? (
                        <CircularProgress size={14} sx={{ mr: 0.5 }} />
                      ) : (
                        'Generate Report'
                      )}
                      {completedCount > 0 && (
                        <Typography variant="caption" sx={{ ml: 0.5 }}>
                          ({completedCount})
                        </Typography>
                      )}
                    </Button>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Completion Dialog */}
      <Dialog
        open={completionDialog}
        onClose={() => setCompletionDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle>
          {existingCompletion ? 'View Container Inspection' : 'Complete Container Inspection'}
          {selectedContainer && (
            <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
              <Typography variant="body2" color="textSecondary">
                Container: {selectedContainer.container_no}
              </Typography>

              {/* Show PO information */}
              {selectedContainer.is_shared ? (
                <Chip
                  label={`${selectedContainer.po_numbers.length} Purchase Orders`}
                  color="info"
                  size="small"
                />
              ) : selectedContainer.primary_po_number ? (
                <Chip
                  label={`PO ${selectedContainer.primary_po_number}`}
                  size="small"
                  variant="outlined"
                />
              ) : null}

              {existingCompletion && (
                <Chip
                  label="Completed"
                  color="success"
                  size="small"
                  icon={<CheckCircleIcon />}
                />
              )}
            </Box>
          )}
        </DialogTitle>

        <DialogContent>
          {existingCompletion ? (
            /* Read-only view for completed inspections */
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Inspection completed on {new Date(existingCompletion.completed_at).toLocaleDateString()} at {new Date(existingCompletion.completed_at).toLocaleTimeString()}
                </Alert>
              </Grid>

              {/* Arrival Time, Departure Time, and Inspection Results - 3 Column Layout */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>Arrival Time</Typography>
                  <Typography variant="body1">
                    {existingCompletion.arrival_time
                      ? new Date(existingCompletion.arrival_time).toLocaleString('en-AU', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })
                      : 'Not recorded'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>Departure Time</Typography>
                  <Typography variant="body1">
                    {existingCompletion.departure_time
                      ? new Date(existingCompletion.departure_time).toLocaleString('en-AU', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })
                      : 'Not recorded'}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>Inspection Results</Typography>
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        Seal Verified and Intact?
                      </Typography>
                      <Box display="flex" alignItems="center" sx={{ mt: 0.5 }}>
                        {existingCompletion.seal_verified ? <CheckCircleIcon color="success" /> : <CloseIcon color="error" />}
                        <Typography sx={{ ml: 1, fontWeight: 500 }}>
                          {existingCompletion.seal_verified ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        Container Free of Water?
                      </Typography>
                      <Box display="flex" alignItems="center" sx={{ mt: 0.5 }}>
                        {existingCompletion.water_free ? <CheckCircleIcon color="success" /> : <CloseIcon color="error" />}
                        <Typography sx={{ ml: 1, fontWeight: 500 }}>
                          {existingCompletion.water_free ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        Pallets in Good Condition?
                      </Typography>
                      <Box display="flex" alignItems="center" sx={{ mt: 0.5 }}>
                        {existingCompletion.condition_ok ? <CheckCircleIcon color="success" /> : <CloseIcon color="error" />}
                        <Typography sx={{ ml: 1, fontWeight: 500 }}>
                          {existingCompletion.condition_ok ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Completed By, Seal Photo, and Concern Photos - 3 Column Layout */}
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Typography variant="h6" gutterBottom>Completed By</Typography>
                  {existingCompletion.signature_url && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <img
                        src={existingCompletion.signature_url}
                        alt="Driver Signature"
                        style={{ maxWidth: '100%', maxHeight: 150, border: '1px solid #ddd', borderRadius: 4 }}
                      />
                    </Box>
                  )}
                  <Typography variant="body1" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {existingCompletion.signed_by_name || existingCompletion.signed_by_staff_no}
                  </Typography>
                </Paper>
              </Grid>

              {/* Seal Photo */}
              {savedPhotos.filter(p => p.photo_type === 'seal').length > 0 ? (
                savedPhotos.filter(p => p.photo_type === 'seal').map((photo, index) => (
                  <Grid item xs={12} md={4} key={`seal-${index}`}>
                    <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" gutterBottom>Seal Photo</Typography>
                      <img
                        src={photo.photo_url}
                        alt="Container Seal"
                        style={{ width: '100%', maxHeight: 300, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 4 }}
                      />
                    </Paper>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12} md={4}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>Seal Photo</Typography>
                    <Typography variant="body2" color="textSecondary">No seal photo available</Typography>
                  </Paper>
                </Grid>
              )}

              {/* Concern Photos */}
              {savedPhotos.filter(p => p.photo_type === 'damage').length > 0 ? (
                <Grid item xs={12} md={4}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>Concern Photos</Typography>
                    <Box>
                      {savedPhotos.filter(p => p.photo_type === 'damage').map((photo, index) => (
                        <Box key={`damage-${index}`} sx={{ mb: index < savedPhotos.filter(p => p.photo_type === 'damage').length - 1 ? 2 : 0 }}>
                          <img
                            src={photo.photo_url}
                            alt={`Concern ${index + 1}`}
                            style={{ width: '100%', maxHeight: 250, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 4 }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              ) : (
                <Grid item xs={12} md={4}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                    <Typography variant="h6" gutterBottom>Concern Photos</Typography>
                    <Typography variant="body2" color="textSecondary">No concern photos</Typography>
                  </Paper>
                </Grid>
              )}

              {/* Container Contents */}
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Container Contents</Typography>
                  {containerLines.length > 0 ? (
                    <Box>
                      {groupContainerLines().map((groupedLine) => {
                        const quantityDisplay = groupedLine.isReel
                          ? `${groupedLine.totalWeightKg.toLocaleString()} kg`
                          : `${groupedLine.totalSheets.toLocaleString()} sheets`;

                        // Get put-away locations for this stock code
                        const stockLocations = savedLineLocations.filter(loc => loc.stock_code === groupedLine.stockCode);

                        return (
                          <Box
                            key={groupedLine.stockCode}
                            sx={{
                              mb: 2,
                              p: 1.5,
                              backgroundColor: '#f5f5f5',
                              borderRadius: 1,
                              border: '1px solid #e0e0e0'
                            }}
                          >
                            <Box>
                              <Typography variant="body1" fontWeight="bold">
                                {groupedLine.stockCode}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Total: {groupedLine.totalPallets} {groupedLine.isReel ? 'reels' : 'pallets'} • {quantityDisplay}
                              </Typography>

                              {/* Put-away locations */}
                              {stockLocations.length > 0 && (
                                <Box sx={{ mt: 1, pl: 2, borderLeft: '3px solid #CB6015' }}>
                                  <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                                    Put-Away Locations:
                                  </Typography>
                                  {stockLocations.map((loc, index) => (
                                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                      <strong>{loc.location_name}:</strong> {loc.quantity} {groupedLine.isReel ? 'reels' : 'pallets'}
                                      {loc.measurement_value && (
                                        <span> • {loc.measurement_value.toLocaleString()} {loc.measurement_unit}</span>
                                      )}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography color="textSecondary">No container lines available</Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          ) : (
            /* Editable form for new inspections */
            <>
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error.split('\n').map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* 1. Arrival Time */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Arrival Time"
                type="datetime-local"
                value={completionData.arrival_time}
                onChange={(e) => setCompletionData(prev => ({
                  ...prev,
                  arrival_time: e.target.value
                }))}
                onBlur={() => saveDraft({ arrival_time: completionData.arrival_time })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* 2. Departure Time */}
            <Grid item xs={12} md={6}>
              <Box display="flex" gap={1} alignItems="flex-start">
                <TextField
                  fullWidth
                  label="Departure Time"
                  type="datetime-local"
                  value={completionData.departure_time}
                  onChange={(e) => setCompletionData(prev => ({
                    ...prev,
                    departure_time: e.target.value
                  }))}
                  onBlur={() => saveDraft({ departure_time: completionData.departure_time })}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    const now = getLocalDateTimeString();
                    setCompletionData(prev => ({
                      ...prev,
                      departure_time: now
                    }));
                    // Auto-save after clicking "Now" button
                    setTimeout(() => saveDraft({ departure_time: now }), 100);
                  }}
                  sx={{ mt: 0.5, minWidth: 80 }}
                >
                  Now
                </Button>
              </Box>
            </Grid>

            {/* 3. Photo of Seal (Left Column) */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 1.5, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Photo of Seal</Typography>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  ref={sealFileInputRef}
                  onChange={handleSealPhotoCapture}
                />
                {(() => {
                  const savedSealPhoto = savedPhotos.find(p => p.photo_type === 'seal');
                  const isUploading = sealPhoto !== null; // Photo is being uploaded

                  return (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<CameraIcon />}
                        onClick={() => sealFileInputRef.current?.click()}
                        size="large"
                        sx={{ mb: 2 }}
                        disabled={isUploading}
                      >
                        {isUploading ? 'Uploading...' : savedSealPhoto ? 'Retake Seal Photo' : 'Capture Seal Photo'}
                      </Button>
                      {/* Show upload preview while uploading */}
                      {sealPhotoPreview && (
                        <Box>
                          <img
                            src={sealPhotoPreview}
                            alt="Uploading..."
                            style={{ width: '100%', maxWidth: 250, height: 'auto', borderRadius: 4, opacity: 0.7 }}
                          />
                          <Typography variant="caption" color="textSecondary">Uploading...</Typography>
                        </Box>
                      )}
                      {/* Show saved seal photo */}
                      {!sealPhotoPreview && savedSealPhoto && (
                        <Box>
                          <img
                            src={savedSealPhoto.photo_url}
                            alt="Seal"
                            style={{ width: '100%', maxWidth: 250, height: 'auto', borderRadius: 4 }}
                          />
                          <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                            ✓ Photo saved
                          </Typography>
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Paper>
            </Grid>

            {/* 4. Inspection Checklist (Right Column) */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 1.5, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Inspection Checklist</Typography>
                <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
                  <strong>Seal:</strong> {selectedContainer?.seal_no || 'Not provided'}
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  {/* Seal Verified */}
                  <Box>
                    <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                      Seal Verified and Intact?
                    </Typography>
                    <RadioGroup
                      row
                      value={completionData.seal_verified === null ? '' : completionData.seal_verified.toString()}
                      onChange={(e) => {
                        const value = e.target.value === 'true';
                        setCompletionData(prev => ({
                          ...prev,
                          seal_verified: value
                        }));
                        saveDraft({ seal_verified: value });
                      }}
                    >
                      <FormControlLabel value="true" control={<Radio size="medium" />} label="Yes" />
                      <FormControlLabel value="false" control={<Radio size="medium" />} label="No" />
                    </RadioGroup>
                  </Box>

                  {/* Container Free of Water */}
                  <Box>
                    <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                      Container Free of Water?
                    </Typography>
                    <RadioGroup
                      row
                      value={completionData.water_free === null ? '' : completionData.water_free.toString()}
                      onChange={(e) => {
                        const value = e.target.value === 'true';
                        setCompletionData(prev => ({
                          ...prev,
                          water_free: value
                        }));
                        saveDraft({ water_free: value });
                      }}
                    >
                      <FormControlLabel value="true" control={<Radio size="medium" />} label="Yes" />
                      <FormControlLabel value="false" control={<Radio size="medium" />} label="No" />
                    </RadioGroup>
                  </Box>

                  {/* Pallets Condition */}
                  <Box>
                    <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                      Pallets in good condition (Free of damage, mould, dirt, water etc.)?
                    </Typography>
                    <RadioGroup
                      row
                      value={completionData.condition_ok === null ? '' : completionData.condition_ok.toString()}
                      onChange={(e) => {
                        const value = e.target.value === 'true';
                        setCompletionData(prev => ({
                          ...prev,
                          condition_ok: value
                        }));
                        saveDraft({ condition_ok: value });
                      }}
                    >
                      <FormControlLabel value="true" control={<Radio size="medium" />} label="Yes" />
                      <FormControlLabel value="false" control={<Radio size="medium" />} label="No" />
                    </RadioGroup>
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* 5. Container Contents and Put-Away Locations */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>Put-Away Locations</Typography>
                {containerLines.length > 0 ? (
                  <>
                    {groupContainerLines().map((groupedLine) => {
                      const stockLocations = lineLocations.filter(l => l.stockCode === groupedLine.stockCode);
                      const totalRecorded = stockLocations.reduce((sum, loc) => sum + (loc.quantity || 0), 0);
                      const remaining = groupedLine.totalPallets - totalRecorded;

                      const quantityDisplay = groupedLine.isReel
                        ? `${groupedLine.totalWeightKg.toLocaleString()} kg`
                        : `${groupedLine.totalSheets.toLocaleString()} sheets`;

                      // Get recommendations for this stock code
                      const stockRecommendations = preplanRecommendations.filter(
                        r => r.stock_code === groupedLine.stockCode
                      );

                      // Determine if this row needs highlighting
                      const isIncomplete = remaining > 0;
                      const isOver = totalRecorded > groupedLine.totalPallets;
                      const hasNoLocations = totalRecorded === 0;

                      return (
                        <Box
                          key={groupedLine.stockCode}
                          sx={{
                            mb: 1.5,
                            p: 1,
                            border: (isIncomplete || isOver) ? '2px solid #d32f2f' : '1px solid #e0e0e0',
                            borderRadius: 1,
                            backgroundColor: (isIncomplete || isOver) ? '#ffebee' : '#fafafa',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: (isIncomplete || isOver) ? '#d32f2f' : 'inherit' }}>
                                {groupedLine.stockCode}
                                {hasNoLocations && <span style={{ marginLeft: 8, fontSize: '0.85rem' }}>⚠️ No put-away recorded</span>}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {groupedLine.totalPallets} {groupedLine.isReel ? 'reels' : 'pallets'} • {quantityDisplay}
                              </Typography>
                            </Box>
                            {remaining > 0 && (
                              <Chip
                                label={`${remaining} remaining`}
                                size="small"
                                color="error"
                                sx={{ fontWeight: 'bold' }}
                              />
                            )}
                            {isOver && (
                              <Chip label={`Over by ${totalRecorded - groupedLine.totalPallets}`} size="small" color="error" sx={{ fontWeight: 'bold' }} />
                            )}
                            {remaining === 0 && !isOver && totalRecorded > 0 && (
                              <Chip label="Complete" size="small" color="success" />
                            )}
                          </Box>

                          {/* Pre-planned location recommendations */}
                          {stockRecommendations.length > 0 && (
                            <Box sx={{ mb: 1, p: 1, backgroundColor: '#e8f4f8', borderRadius: 1, border: '1px dashed #0288d1' }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#0277bd', display: 'block', mb: 0.5 }}>
                                📍 Recommended Locations (Pre-Planned)
                              </Typography>
                              {stockRecommendations.map((rec, idx) => (
                                <Box key={idx} sx={{ mb: 0.5 }}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="caption" sx={{ flex: 1 }}>
                                      <strong>{rec.location_name}</strong> - {rec.is_flexible
                                        ? <span style={{ fontStyle: 'italic' }}>Use as needed</span>
                                        : `${rec.recommended_quantity} ${groupedLine.isReel ? 'reels' : 'pallets'}`
                                      }
                                      {rec.notes && <span style={{ fontStyle: 'italic', color: '#d32f2f', fontWeight: 500 }}> ({rec.notes})</span>}
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => applyRecommendation(rec)}
                                      sx={{ fontSize: '0.7rem', py: 0.25, px: 1 }}
                                    >
                                      Apply
                                    </Button>
                                  </Box>
                                  {!rec.is_flexible && rec.consumed_quantity > 0 && (
                                    <Typography variant="caption" sx={{ color: '#0288d1', display: 'block', ml: 1, fontSize: '0.65rem' }}>
                                      ↳ {rec.consumed_quantity} already placed, {rec.remaining_quantity} remaining of {rec.planned_quantity} planned
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                              <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                Planned by: {stockRecommendations[0].created_by_name || 'Despatch Manager'}
                              </Typography>
                            </Box>
                          )}

                          {stockLocations.map((locEntry) => {
                            const globalIndex = lineLocations.indexOf(locEntry);
                            return (
                              <Box key={globalIndex} display="flex" gap={1} sx={{ mb: 0.5 }}>
                                <TextField
                                  size="small"
                                  placeholder="Location"
                                  value={locEntry.location}
                                  onChange={(e) => updateLocationEntry(globalIndex, 'location', e.target.value.toUpperCase())}
                                  onBlur={() => saveLineLocationsDraft()}
                                  sx={{ flex: 1 }}
                                />
                                <TextField
                                  size="small"
                                  type="number"
                                  placeholder="Qty"
                                  value={locEntry.quantity || ''}
                                  onChange={(e) => updateLocationEntry(globalIndex, 'quantity', Number(e.target.value))}
                                  onBlur={() => saveLineLocationsDraft()}
                                  sx={{ width: 200 }}
                                  InputProps={{
                                    endAdornment: <Typography variant="caption" sx={{ ml: 0.5 }}>{groupedLine.isReel ? 'reels' : 'pallets'}</Typography>
                                  }}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => removeLocationEntry(globalIndex)}
                                  color="error"
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Box>
                            );
                          })}

                          <Button
                            size="small"
                            onClick={() => addLocationEntry(groupedLine.stockCode)}
                            variant="text"
                            sx={{ mt: 0.5, textTransform: 'none' }}
                          >
                            + Add Location
                          </Button>
                        </Box>
                      );
                    })}
                  </>
                ) : (
                  <Typography color="textSecondary">No container lines available</Typography>
                )}
              </Paper>
            </Grid>

            {/* 6. Photos of Concerns (Left Column) */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 1.5, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>Concern Photos</Typography>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  ref={concernFileInputRef}
                  onChange={handleConcernPhotosCapture}
                />
                <Button
                  variant="outlined"
                  startIcon={<CameraIcon />}
                  onClick={() => concernFileInputRef.current?.click()}
                  size="small"
                  sx={{ mb: 1 }}
                >
                  Add Photos ({concernPhotos.length})
                </Button>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {concernPhotosPreviews.map((photoUrl, index) => (
                    <img
                      key={index}
                      src={photoUrl}
                      alt={`Concern ${index + 1}`}
                      style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* 7. Forklift Driver Signature (Right Column) */}
            <Grid item xs={12} md={6}>
              <SignaturePad
                onSave={handleSignatureSave}
                onClear={() => setCompletionData(prev => ({ ...prev, signature_url: '' }))}
              />
            </Grid>
          </Grid>
          </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCompletionDialog(false)}>
            {existingCompletion ? 'Close' : 'Cancel'}
          </Button>
          {!existingCompletion && (
            <Button
              onClick={submitCompletion}
              variant="contained"
              color="primary"
              disabled={!completionData.signature_url}
            >
              Complete Inspection
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Daily Container Report Dialog */}
      <DailyContainerReport
        open={dailyReportOpen}
        onClose={() => setDailyReportOpen(false)}
        reportDate={dailyReportDate}
        containers={dailyReportData}
      />
    </Box>
  );
};

export default ForkliftContainerApp;